import { BUSINESS_BRIEF_TIMEZONE_OPTIONS } from '@doneverse/shared/validation'
import type { SchemaField } from './schema-field.js'

const NON_ECOMMERCE_INDUSTRIES = [
  'Coach/Consultant',
  'Professional Service',
  'Local Service',
  'Marketing Agency',
  'Med Spa',
  'Real Estate',
]

export const BUSINESS_BRIEF_SCHEMA: SchemaField[] = [
  // ── Section 1: Your Details ──────────────────────────────────
  { id: 'section_details', type: 'section', title: 'Your Details' },
  { id: 'company', question: 'Company', field_type: 'short_text', required: true },

  // ── Section 2: Industry + Sub-Niche ──────────────────────────
  { id: 'section_industry', type: 'section', title: 'Industry' },
  {
    id: 'industry',
    question: 'What industry are you in?',
    field_type: 'single_select',
    required: true,
    options: [
      'Coach/Consultant',
      'Professional Service',
      'Local Service',
      'Marketing Agency',
      'Med Spa',
      'Real Estate',
      'eCommerce',
    ],
  },
  {
    id: 'subNiche_coach',
    question: 'What type of coaching or consulting?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'Coach/Consultant' },
    required: true,
    options: [
      'Life Coach',
      'Business Coach',
      'Health & Wellness Coach',
      'Executive Coach',
      'Career Coach',
      'Relationship Coach',
      'Financial Coach',
      'Leadership Consultant',
      'Management Consultant',
      'Strategy Consultant',
      'IT Consultant',
      'HR Consultant',
      'Other',
    ],
  },
  {
    id: 'subNiche_coach_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'subNiche_coach', equals: 'Other' },
    required: true,
  },
  {
    id: 'subNiche_professional',
    question: 'What type of professional service?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'Professional Service' },
    required: true,
    options: [
      'Accounting / CPA',
      'Legal / Law Firm',
      'Financial Planning',
      'Architecture',
      'Engineering',
      'Medical Practice',
      'Dental Practice',
      'Therapy / Counseling',
      'Veterinary',
      'Other',
    ],
  },
  {
    id: 'subNiche_professional_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'subNiche_professional', equals: 'Other' },
    required: true,
  },
  {
    id: 'subNiche_local',
    question: 'What type of local service?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'Local Service' },
    required: true,
    options: [
      'Home Services (Plumbing, HVAC, Electrical)',
      'Cleaning Services',
      'Landscaping / Lawn Care',
      'Auto Repair / Detailing',
      'Pest Control',
      'Moving / Storage',
      'Beauty / Salon / Spa',
      'Fitness / Gym',
      'Pet Services',
      'Other',
    ],
  },
  {
    id: 'subNiche_local_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'subNiche_local', equals: 'Other' },
    required: true,
  },
  {
    id: 'subNiche_agency',
    question: 'What type of marketing agency?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'Marketing Agency' },
    required: true,
    options: [
      'Full-Service Digital Agency',
      'SEO Agency',
      'Social Media Agency',
      'PPC / Paid Ads Agency',
      'Content Marketing Agency',
      'Branding Agency',
      'Web Design / Development',
      'PR Agency',
      'Other',
    ],
  },
  {
    id: 'subNiche_agency_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'subNiche_agency', equals: 'Other' },
    required: true,
  },
  {
    id: 'subNiche_realestate',
    question: 'What type of real estate?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'Real Estate' },
    required: true,
    options: [
      'Residential Sales',
      'Commercial Sales',
      'Property Management',
      'Real Estate Investing',
      'Mortgage / Lending',
      'Other',
    ],
  },
  {
    id: 'subNiche_realestate_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'subNiche_realestate', equals: 'Other' },
    required: true,
  },
  {
    id: 'subNiche_ecommerce',
    question: 'What type of eCommerce?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
    required: true,
    options: [
      'Physical Products (DTC)',
      'Dropshipping',
      'Print on Demand',
      'Digital Products',
      'Subscription Box',
      'Marketplace Seller (Amazon, Etsy)',
      'Other',
    ],
  },
  {
    id: 'subNiche_ecommerce_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'subNiche_ecommerce', equals: 'Other' },
    required: true,
  },

  // ── Section 3: Revenue ───────────────────────────────────────
  { id: 'section_revenue', type: 'section', title: 'Revenue' },
  {
    id: 'monthlyRevenue',
    question: 'What is your current average monthly revenue?',
    field_type: 'single_select',
    required: true,
    options: [
      'Pre Revenue',
      '$1K - $2K / month',
      '$2K - $5K / month',
      '$5K - $8K / month',
      '$8K - $10K / month',
      '$10K - $20K / month',
      '$20K - $40K / month',
      '$40K - $60K / month',
      '$60K - $80K / month',
      '$80K - $100K / month',
      '$100K - $200K / month',
      '$200K - $500K / month',
      '$500K / month +',
    ],
  },

  // ── Section 4: Digital Assets ────────────────────────────────
  { id: 'section_digital', type: 'section', title: 'Digital Assets' },
  {
    id: 'website',
    question: 'Website URL',
    field_type: 'short_text',
    required: false,
  },
  {
    id: 'socialMedia',
    question: 'Social Media links',
    field_type: 'short_text',
    displayAs: 'multi_list',
    required: false,
  },
  {
    id: 'otherLinks',
    question: 'Other links (e.g. portfolio, calendly, etc.)',
    field_type: 'short_text',
    required: false,
  },

  // ── Section 5: About Your Business ──────────────────────────
  { id: 'section_about', type: 'section', title: 'About Your Business' },
  {
    id: 'businessStatement',
    question: 'Tell us about your business — what you do, who you serve, and what makes you different',
    field_type: 'long_text',
    displayAs: 'long_text_xl',
    required: true,
  },
  {
    id: 'top3Services',
    question: 'What are your top 3 services or products?',
    field_type: 'long_text',
    required: true,
  },
  {
    id: 'teamSize',
    question: 'How many people are on your team (including you)?',
    field_type: 'single_select',
    required: true,
    options: ['Just me', '2-3', '4-5', '6-10', '11-25', '26-50', '50+'],
  },
  {
    id: 'whyDoer',
    question: 'Why are you investing in a Doer?',
    field_type: 'single_select',
    required: true,
    options: [
      'I need help executing my marketing',
      'I want to free up my time',
      "I need specialized skills I don't have",
      'I want to scale faster',
      'I want to test new channels',
      'Other',
    ],
  },
  {
    id: 'whyDoer_other',
    question: 'Please specify',
    field_type: 'short_text',
    showWhen: { questionId: 'whyDoer', equals: 'Other' },
    required: true,
  },
  {
    id: 'whyDoneverse',
    question: 'Why did you choose Doneverse?',
    field_type: 'long_text',
    required: false,
  },

  // ── Section 6: Your Outsourcing Adventure ───────────────────
  { id: 'section_outsourcing', type: 'section', title: 'Your Outsourcing Adventure' },
  {
    id: 'timezone',
    question: 'What timezone does your business primarily operate in?',
    field_type: 'single_select',
    // Long list (curated zones + full IANA database) — render as a searchable combobox.
    displayAs: 'searchable_select',
    required: true,
    options: BUSINESS_BRIEF_TIMEZONE_OPTIONS,
  },
  {
    id: 'blueprintPriority',
    question: 'Rank the Blueprint steps in order of priority for your business',
    description: 'Drag to reorder, or use the arrow buttons',
    field_type: 'rank',
    required: true,
    options: [
      'Attract Traffic',
      'Capture Leads',
      'Nurture Prospects',
      'Convert Sales',
      'Deliver & WOW',
      'Upsell & Retain',
      'Get Referrals',
    ],
  },
  {
    id: 'project30Days_nonEcomm',
    question: 'If you could get ONE project done in the next 30 days, what would it be?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    required: true,
    options: [
      'Build / Redesign my website',
      'Set up email marketing',
      'Launch a social media strategy',
      'Create a lead magnet / funnel',
      'Build a course or digital product',
      'Rebrand my business',
      'Other',
    ],
  },
  {
    id: 'project30Days_nonEcomm_other',
    question: 'Please describe your project',
    field_type: 'short_text',
    showWhen: { questionId: 'project30Days_nonEcomm', equals: 'Other' },
    required: true,
  },
  {
    id: 'project30Days_ecomm',
    question: 'If you could get ONE project done in the next 30 days, what would it be?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
    required: true,
    options: [
      'Build / Redesign my online store',
      'Optimize product listings',
      'Set up email marketing',
      'Launch paid ads (Meta, Google)',
      'Improve conversion rate',
      'Set up inventory management',
      'Other',
    ],
  },
  {
    id: 'project30Days_ecomm_other',
    question: 'Please describe your project',
    field_type: 'short_text',
    showWhen: { questionId: 'project30Days_ecomm', equals: 'Other' },
    required: true,
  },
  {
    id: 'project12Months',
    question: 'What is your biggest business goal in the next 12 months?',
    field_type: 'long_text',
    required: true,
  },
  {
    id: 'tasksToOffload',
    question: 'What tasks would you love to take off your plate?',
    field_type: 'multi_select',
    required: true,
    options: [
      'Social media management',
      'Email marketing',
      'Website updates',
      'Graphic design',
      'Video editing',
      'Customer support',
      'Admin / data entry',
    ],
  },

  // ── Section 7: Your Branding ────────────────────────────────
  { id: 'section_branding', type: 'section', title: 'Your Branding' },
  {
    id: 'hasBrandGuide',
    question: 'Do you have a brand style guide?',
    field_type: 'single_select',
    displayAs: 'radio',
    required: true,
    options: ['Yes', 'No'],
  },
  {
    id: 'brandGuideLink',
    question: 'Share a link to your brand style guide or shared folder',
    field_type: 'short_text',
    showWhen: { questionId: 'hasBrandGuide', equals: 'Yes' },
    required: true,
  },
  {
    id: 'logoLink',
    question: 'Share a link to your logo files',
    field_type: 'short_text',
    showWhen: { questionId: 'hasBrandGuide', equals: 'No' },
    required: false,
  },
  {
    id: 'brandColors',
    question: 'What are your brand HEX color codes?',
    field_type: 'short_text',
    showWhen: { questionId: 'hasBrandGuide', equals: 'No' },
    required: false,
  },
  {
    id: 'brandFonts',
    question: 'What fonts does your brand use?',
    field_type: 'short_text',
    showWhen: { questionId: 'hasBrandGuide', equals: 'No' },
    required: false,
  },
  {
    id: 'brandImages',
    question: 'Share a link to brand images / photos',
    field_type: 'short_text',
    showWhen: { questionId: 'hasBrandGuide', equals: 'No' },
    required: false,
  },
  {
    id: 'brandGuidelines',
    question: 'Any other brand guidelines or notes?',
    field_type: 'short_text',
    showWhen: { questionId: 'hasBrandGuide', equals: 'No' },
    required: false,
  },

  // ── Section 8: Doer Training ────────────────────────────────
  {
    id: 'section_training_ecomm',
    type: 'section',
    title: 'Doer Training',
    description:
      'Your Doer will complete mandatory eCommerce badges. Select any additional training badges below.',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
  },
  {
    id: 'section_training_nonEcomm',
    type: 'section',
    title: 'Doer Training',
    description:
      'Your Doer will complete mandatory service-business badges. Select any additional training badges below.',
    showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
  },
  {
    id: 'additionalBadges_ecomm',
    question: 'Select additional training badges for your Doer',
    field_type: 'multi_select',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
    required: false,
    options: [
      'Shopify Store Setup',
      'WooCommerce Setup',
      'Product Photography',
      'Product Listing Optimization',
      'Amazon FBA',
      'Etsy Seller',
      'Inventory Management',
      'Order Fulfillment',
      'Returns & Customer Service',
      'Meta Ads (Facebook/Instagram)',
      'Google Shopping Ads',
      'TikTok Shop',
      'Email Marketing (Klaviyo)',
      'SMS Marketing',
      'Conversion Rate Optimization',
      'Analytics & Reporting',
    ],
  },
  {
    id: 'additionalBadges_nonEcomm',
    question: 'Select additional training badges for your Doer',
    field_type: 'multi_select',
    showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    required: false,
    options: [
      'WordPress / Website Management',
      'Landing Page Design',
      'SEO Fundamentals',
      'Google Business Profile',
      'Social Media Management',
      'Content Creation (Graphics)',
      'Content Creation (Video)',
      'Copywriting',
      'Email Marketing',
      'CRM Setup & Management',
      'Funnel Building',
      'Meta Ads (Facebook/Instagram)',
      'Google Ads',
      'YouTube Ads',
      'LinkedIn Outreach',
      'Podcast Production',
      'Webinar Setup',
      'Community Management',
      'Reputation Management',
      'Analytics & Reporting',
      'Project Management',
      'AI Tools & Automation',
    ],
  },

  // ── Section 9: Tools ────────────────────────────────────────
  {
    id: 'section_tools',
    type: 'section',
    title: 'Tools',
    description: 'Tell us which tools your business uses so your Doer can hit the ground running.',
  },

  // eCommerce tools
  {
    id: 'tool_website_ecomm',
    question: 'What platform is your online store on?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
    required: true,
    options: ['Shopify', 'WooCommerce', 'BigCommerce', 'Squarespace', 'Wix', 'Other'],
  },
  {
    id: 'tool_website_ecomm_other',
    question: 'Please specify the platform',
    field_type: 'short_text',
    showWhen: { questionId: 'tool_website_ecomm', equals: 'Other' },
    required: true,
  },
  {
    id: 'tool_inventory_ecomm',
    question: 'What inventory / fulfillment tool do you use?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
    required: false,
    options: ['ShipStation', 'ShipBob', 'Deliverr', 'Built-in (Shopify/WooCommerce)', 'None', 'Other'],
  },
  {
    id: 'tool_inventory_ecomm_other',
    question: 'Please specify the tool',
    field_type: 'short_text',
    showWhen: { questionId: 'tool_inventory_ecomm', equals: 'Other' },
    required: true,
  },
  {
    id: 'tool_email_ecomm',
    question: 'What email marketing tool do you use?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: 'eCommerce' },
    required: false,
    options: ['Klaviyo', 'Mailchimp', 'Omnisend', 'ActiveCampaign', 'None', 'Other'],
  },
  {
    id: 'tool_email_ecomm_other',
    question: 'Please specify the tool',
    field_type: 'short_text',
    showWhen: { questionId: 'tool_email_ecomm', equals: 'Other' },
    required: true,
  },

  // Non-eCommerce tools
  {
    id: 'tool_email_nonEcomm',
    question: 'What email marketing tool do you use?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    required: false,
    options: ['Mailchimp', 'ActiveCampaign', 'ConvertKit', 'HubSpot', 'Constant Contact', 'None', 'Other'],
  },
  {
    id: 'tool_email_nonEcomm_other',
    question: 'Please specify the tool',
    field_type: 'short_text',
    showWhen: { questionId: 'tool_email_nonEcomm', equals: 'Other' },
    required: true,
  },
  {
    id: 'tool_landingPage_nonEcomm',
    question: 'What landing page / funnel tool do you use?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    required: false,
    options: ['ClickFunnels', 'Leadpages', 'Unbounce', 'Kartra', 'None', 'Other'],
  },
  {
    id: 'tool_landingPage_nonEcomm_other',
    question: 'Please specify the tool',
    field_type: 'short_text',
    showWhen: { questionId: 'tool_landingPage_nonEcomm', equals: 'Other' },
    required: true,
  },
  {
    id: 'tool_website_nonEcomm',
    question: 'What platform is your website on?',
    field_type: 'single_select',
    showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    required: false,
    options: ['WordPress', 'Squarespace', 'Wix', 'Webflow', 'Custom-built', 'None', 'Other'],
  },
  {
    id: 'tool_website_nonEcomm_other',
    question: 'Please specify the platform',
    field_type: 'short_text',
    showWhen: { questionId: 'tool_website_nonEcomm', equals: 'Other' },
    required: true,
  },
]
